import React from "react";
import { Provider as ReduxProvider, useDispatch, useStore as useReduxStore, useSelector, } from "react-redux";
import { getIdFromAction } from "./action.js";
import { PERSIST_LOADER_ID, } from "./store/index.js";
export { useDispatch, useSelector } from "react-redux";
const { useContext, useEffect, useRef, createContext, createElement: h, } = React;
const SchemaContext = createContext(null);
export function Provider({ store, schema, children, }) {
    return h(ReduxProvider, {
        store,
        children: h(SchemaContext.Provider, { value: schema, children }),
    });
}
export function useSchema() {
    return useContext(SchemaContext);
}
export function useStore() {
    return useReduxStore();
}
/**
 * useLoader will take an action creator or action itself and return the associated
 * loader for it.
 *
 * @returns the loader object for an action creator or action
 *
 * @example
 * ```ts
 * import { useLoader } from 'starfx/react';
 *
 * import { api } from './api';
 *
 * const fetchUsers = api.get('/users', function*() {
 *   // ...
 * });
 *
 * const View = () => {
 *   const loader = useLoader(fetchUsers);
 *   // or: const loader = useLoader(fetchUsers());
 *   return <div>{loader.isLoader ? 'Loading ...' : 'Done!'}</div>
 * }
 * ```
 */
export function useLoader(action) {
    const schema = useSchema();
    const id = getIdFromAction(action);
    return useSelector((s) => schema.loaders.selectById(s, { id }));
}
export function useApi(action) {
    const dispatch = useDispatch();
    const loader = useLoader(action);
    const trigger = (p) => {
        if (typeof action === "function") {
            dispatch(action(p));
        }
        else {
            dispatch(action);
        }
    };
    return { ...loader, trigger, action };
}
/**
 * useQuery uses {@link useApi} and automatically calls `useApi().trigger()`
 *
 * @example
 * ```ts
 * import { useQuery } from 'starfx/react';
 *
 * import { api } from './api';
 *
 * const fetchUsers = api.get('/users', function*() {
 *   // ...
 * });
 *
 * const View = () => {
 *   const { isLoading } = useQuery(fetchUsers);
 *   return <div>{isLoading ? : 'Loading' : 'Done!'}</div>
 * }
 * ```
 */
export function useQuery(action) {
    const api = useApi(action);
    useEffect(() => {
        api.trigger();
    }, [action.payload.key]);
    return api;
}
/**
 * useCache uses {@link useQuery} and automatically selects the cached data associated
 * with the action creator or action provided.
 *
 * @example
 * ```ts
 * import { useCache } from 'starfx/react';
 *
 * import { api } from './api';
 *
 * const fetchUsers = api.get('/users', api.cache());
 *
 * const View = () => {
 *   const { isLoading, data } = useCache(fetchUsers());
 *   return <div>{isLoading ? : 'Loading' : data.length}</div>
 * }
 * ```
 */
export function useCache(action) {
    const schema = useSchema();
    const id = action.payload.key;
    const data = useSelector((s) => schema.cache.selectById(s, { id }));
    const query = useQuery(action);
    return { ...query, data: data || null };
}
/**
 * useLoaderSuccess will activate the callback provided when the loader transitions
 * from some state to success.
 *
 * @example
 * ```ts
 * import { useLoaderSuccess, useApi } from 'starfx/react';
 *
 * import { api } from './api';
 *
 * const createUser = api.post('/users', function*(ctx, next) {
 *   // ...
 * });
 *
 * const View = () => {
 *  const { loader, trigger } = useApi(createUser);
 *  const onSubmit = () => {
 *    trigger({ name: 'bob' });
 *  };
 *
 *  useLoaderSuccess(loader, () => {
 *    // success!
 *    // Use this callback to navigate to another view
 *  });
 *
 *  return <button onClick={onSubmit}>Create user!</button>
 * }
 * ```
 */
export function useLoaderSuccess(cur, success) {
    const prev = useRef(cur);
    useEffect(() => {
        if (prev.current.status !== "success" && cur.status === "success") {
            success();
        }
        prev.current = cur;
    }, [cur.status]);
}
function Loading({ text }) {
    return h("div", null, text);
}
export function PersistGate({ children, loading = h(Loading), }) {
    const schema = useSchema();
    const ldr = useSelector((s) => schema.loaders.selectById(s, { id: PERSIST_LOADER_ID }));
    if (ldr.status === "error") {
        return h("div", null, ldr.message);
    }
    if (ldr.status !== "success") {
        return loading;
    }
    return children;
}
